// One register (RFIs, notices, variations, claims, NDAs) — list with filters; click a row to open the item.
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { fmtDate, fmtMoney } from '../lib/format';
import { isClosed, itemFields, registerFor, statusLabel, STATUS, type Item } from '../lib/registers';
import { Badge, Button, Card, Empty, PageHeader } from '../ui/kit';
import { RecordDrawer } from '../ui/RecordDrawer';

const NONE = '00000000-0000-0000-0000-000000000000';
const today = () => new Date().toISOString().slice(0, 10);
type Row = Pick<Item, 'id' | 'number' | 'ref_no' | 'subtype' | 'title' | 'status' | 'building_ids' | 'buildings_raw' | 'org_id' | 'org_raw' | 'submitted_on' | 'respond_by' | 'amount' | 'ss_created_at' | 'created_at' | 'updated_at' | 'deleted_at'>;

// "latest" = date submitted, else when it was first entered (Smartsheet or PM C3)
const when = (r: Row) => r.submitted_on ?? (r.ss_created_at ?? r.created_at ?? '').slice(0, 10);
const numOf = (r: Row) => Number(/(\d+)\s*$/.exec(r.number ?? '')?.[1] ?? -1);
const SORTS: { key: string; label: string; cmp: (a: Row, b: Row) => number; needs?: 'respond' | 'money' }[] = [
  { key: 'latest', label: 'Latest first', cmp: (a, b) => when(b).localeCompare(when(a)) || numOf(b) - numOf(a) },
  { key: 'oldest', label: 'Oldest first', cmp: (a, b) => when(a).localeCompare(when(b)) || numOf(a) - numOf(b) },
  { key: 'num_desc', label: 'Number — highest first', cmp: (a, b) => numOf(b) - numOf(a) },
  { key: 'num_asc', label: 'Number — lowest first', cmp: (a, b) => numOf(a) - numOf(b) },
  { key: 'topic_az', label: 'Topic A–Z', cmp: (a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }) },
  { key: 'topic_za', label: 'Topic Z–A', cmp: (a, b) => b.title.localeCompare(a.title, 'en', { sensitivity: 'base' }) },
  { key: 'status', label: 'Status', cmp: (a, b) => statusLabel(a.status).localeCompare(statusLabel(b.status)) || when(b).localeCompare(when(a)) },
  { key: 'respond', label: 'Respond by — soonest first', needs: 'respond', cmp: (a, b) => (a.respond_by ?? '9999').localeCompare(b.respond_by ?? '9999') },
  { key: 'amount', label: 'Amount — highest first', needs: 'money', cmp: (a, b) => Number(b.amount ?? 0) - Number(a.amount ?? 0) },
];

export default function Register() {
  const { module } = useParams();
  const def = registerFor(module);
  const { siteId, site, can } = useApp();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? 'all';
  const sort = params.get('sort') ?? 'latest';
  const building = params.get('b') ?? '';
  const setParam = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };

  const list = useRows<Row>(
    () =>
      supabase
        .from('items')
        .select('id, number, ref_no, subtype, title, status, building_ids, buildings_raw, org_id, org_raw, submitted_on, respond_by, amount, ss_created_at, created_at, updated_at, deleted_at')
        .eq('site_id', siteId ?? NONE)
        .eq('type', def?.type ?? 'none')
        .is('deleted_at', null)
        .limit(5000),
    [siteId, def?.type],
    ['items'],
  );
  const buildings = useRows<{ id: string; name: string }>(
    () => supabase.from('buildings').select('id, name').eq('site_id', siteId ?? NONE).is('deleted_at', null).order('sort').order('name'),
    [siteId],
    ['buildings'],
  );
  const orgs = useRows<{ id: string; name: string }>(
    () => supabase.from('organisations').select('id, name').is('deleted_at', null).order('name'),
    [],
    ['organisations'],
  );
  const bName = useMemo(() => new Map(buildings.rows.map((b) => [b.id, b.name])), [buildings.rows]);
  const oName = useMemo(() => new Map(orgs.rows.map((o) => [o.id, o.name])), [orgs.rows]);

  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const cmp = (SORTS.find((x) => x.key === sort) ?? SORTS[0]).cmp;
    return list.rows.filter((r) => {
      if (status === 'open' && isClosed(r.status)) return false;
      if (status !== 'open' && status !== 'all' && r.status !== status) return false;
      if (building && !r.building_ids.includes(building)) return false;
      if (!words.length) return true;
      const hay = [r.number, r.ref_no, r.subtype, r.title, ...r.buildings_raw, r.org_raw].join(' ').toLowerCase();
      return words.every((w) => hay.includes(w));
    }).sort(cmp);
  }, [list.rows, q, status, building, sort]);

  if (!def) return <Navigate to="/" replace />;
  if (!can(def.module)) return <Navigate to="/" replace />;
  const editable = can(def.module, 4);
  const total = def.money ? shown.reduce((s, r) => s + Number(r.amount ?? 0), 0) : 0;
  const t = today();

  return (
    <>
      <PageHeader
        title={def.label}
        sub={site?.name}
        actions={editable ? <Button kind="primary" onClick={() => setAdding(true)}>+ New {def.one}</Button> : null}
      />
      <div className="toolbar">
        <input type="search" placeholder="Find by number, topic, building…" value={q} onChange={(e) => setParam('q', e.target.value)} aria-label="Find" style={{ minWidth: 240 }} />
        <select value={status} onChange={(e) => setParam('status', e.target.value === 'all' ? '' : e.target.value)} aria-label="Status">
          <option value="all">All items</option>
          <option value="open">Open items</option>
          {def.statuses.map((s) => (
            <option key={s} value={s}>
              {STATUS[s]?.label ?? s}
            </option>
          ))}
        </select>
        <select value={sort} onChange={(e) => setParam('sort', e.target.value === 'latest' ? '' : e.target.value)} aria-label="Sort">
          {SORTS.filter((x) => (x.needs === 'respond' ? def.hasRespondBy : x.needs === 'money' ? Boolean(def.money) : true)).map((x) => (
            <option key={x.key} value={x.key}>
              {x.label}
            </option>
          ))}
        </select>
        <select value={building} onChange={(e) => setParam('b', e.target.value)} aria-label="Building">
          <option value="">All buildings</option>
          {buildings.rows.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
      <Card pad={false}>
        {list.error ? <div className="error-box">{list.error}</div> : null}
        {list.loading ? (
          <Empty>Loading…</Empty>
        ) : shown.length === 0 ? (
          <Empty>{list.rows.length ? 'Nothing matches these filters.' : `No ${def.label.toLowerCase()} yet — import them from Smartsheet (Admin → Import) or add one.`}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Topic</th>
                  <th>Buildings</th>
                  {def.hasOrg ? <th>Contractor</th> : null}
                  <th>Status</th>
                  {def.type !== 'nda' ? <th>Submitted</th> : null}
                  {def.hasRespondBy ? <th>Respond by</th> : null}
                  {def.money ? <th className="right">Amount {def.money}</th> : null}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const late = r.respond_by && r.respond_by < t && !isClosed(r.status);
                  const names = r.building_ids.map((id) => bName.get(id)).filter(Boolean) as string[];
                  return (
                    <tr key={r.id} className="click" onClick={() => nav(`/r/${def.module}/${r.id}`)}>
                      <td>
                        <a className="num" href={`/r/${def.module}/${r.id}`} onClick={(e) => (e.preventDefault(), nav(`/r/${def.module}/${r.id}`))}>
                          {r.number ?? '—'}
                        </a>
                        {r.ref_no ? <div className="muted small">{r.ref_no}</div> : null}
                      </td>
                      <td>
                        <div className="clip">{r.title}</div>
                        {r.subtype ? <div className="muted small">{r.subtype}</div> : null}
                      </td>
                      <td className="small">{names.length ? names.join(', ') : r.buildings_raw.join(', ') || '—'}</td>
                      {def.hasOrg ? <td className="small">{(r.org_id && oName.get(r.org_id)) || r.org_raw || '—'}</td> : null}
                      <td>
                        <Badge tone={STATUS[r.status]?.tone ?? 'neutral'}>{statusLabel(r.status)}</Badge>
                      </td>
                      {def.type !== 'nda' ? <td className="small">{fmtDate(r.submitted_on)}</td> : null}
                      {def.hasRespondBy ? <td className={`small ${late ? 'overdue' : ''}`}>{fmtDate(r.respond_by)}{late ? ' · overdue' : ''}</td> : null}
                      {def.money ? <td className="right">{fmtMoney(r.amount)}</td> : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!list.loading && list.rows.length ? (
          <div className="count-line">
            Showing {shown.length} of {list.rows.length}
            {def.money ? ` · total ${fmtMoney(total)} ${def.money}` : ''}
          </div>
        ) : null}
      </Card>

      <RecordDrawer
        open={adding}
        onClose={() => setAdding(false)}
        title={`New ${def.one}`}
        table="items"
        fields={itemFields(def, buildings.rows, orgs.rows)}
        record={null}
        defaults={{ site_id: siteId, type: def.type, status: def.statuses[0], amount_basis: def.money ?? null }}
        canEdit={editable}
        onSaved={(row) => row?.id && nav(`/r/${def.module}/${row.id}`)}
        label={(r) => `${def.one} ${String(r.title ?? '').slice(0, 60)}`}
      />
    </>
  );
}
